import React, {useEffect, useState} from "react";
import {Formik, FormikProps} from "formik";
import * as Yup from "yup";
import {wire} from "react-hot-wire";
import {isUUID} from "validator";

import isDomainName from "utils/validation/isDomainName";

import CircularProgress from "@material-ui/core/CircularProgress";

import {SiteService} from "services/site/Site.service";

import {Application, ApplicationType} from "models/application/Application";

import Drawer from "components/common/drawer/Drawer";

import EditApplication from "./Edit.template";

export type EditApplicationFormValues = {
  environment: string;
  mainDomainName: string | null;
  note: string | null;
  accountManagerNote: string | null;
  redirectDomainNames: string[];
  packages: string[];
  rewardCafeteriaEnabled: boolean;
  workflowsEnabled: boolean;
  oldAdminEnabled: boolean;
  isEnterprise: boolean;
  billingEmail: string;
  billingForename: string;
  billingSurname: string;
  currency: string;
  hubSpotId: string;
  featureFlags: {
      battlesEnabled: boolean;
  }
};

const EditApplicationSchema = Yup.object().shape({
  environment: Yup.string().required(),
  mainDomainName: Yup.string().test(
    "domain-validation",
    "invalid-domain",
    isDomainName(true)
  ),
  note: Yup.string().required(),
  accountManagerNote: Yup.string().required(),
  redirectDomainNames: Yup.array().of(
    Yup.string().test("domain-validation", "invalid-domain", isDomainName(true))
  ),
  packages: Yup.array().of(
    Yup.string().test("uuid-validation", "invalid-uuid", isUUID)
  ),
  rewardCafeteriaEnabled: Yup.boolean().required(),
  workflowsEnabled: Yup.boolean().required(),
  oldAdminEnabled: Yup.boolean().required(),
  isEnterprise: Yup.boolean().required(),
  billingEmail: Yup.string()
    .email()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string()
        .email()
        .required()
    }),
  billingForename: Yup.string()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string().required()
    }),
  billingSurname: Yup.string()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string().required()
    }),
  currency: Yup.string()
    .nullable()
    .when("isEnterprise", {
      is: false,
      then: Yup.string().required()
    }),
  hubSpotId: Yup.string().nullable(),
  featureFlags: Yup.object().shape({
    battlesEnabled: Yup.boolean().required(),
  })
});

function EditApplicationComponent({
  siteService,
  drawerProps,
  action,
  ...props
}: {
  application: Application;
  siteService: SiteService;
  drawerProps?: any;
  action?: () => void;
}) {
  const [application, setApplication] = useState<Application | null>(null);

  useEffect(() => {
    (async () => {
      const application = await siteService.fetchApplication({
        application: props.application
      });

      setApplication(application);
    })();
  }, [props.application, siteService]);

  return (
    <Drawer
      {...drawerProps}
      render={({ close }: { close: () => void }) =>
        application ? (
          <Formik
            enableReinitialize
            initialValues={{
              redirectDomainNames: application.redirectDomainNames,
              packages: application.packages,
              mainDomainName: application.mainDomainName,
              note: application.note,
              accountManagerNote: application.accountManagerNote,
              environment: application.environment,
              rewardCafeteriaEnabled: application.rewardCafeteriaEnabled,
              workflowsEnabled: application.workflowsEnabled,
              oldAdminEnabled: application.oldAdminEnabled,
              isEnterprise: application.type === ApplicationType.ENTERPRISE,
              billingEmail: application.billingEmail,
              billingForename: application.billingForename,
              billingSurname: application.billingSurname,
              currency: application.currency || "usd",
              hubSpotId: application.hubSpotId || "",
              featureFlags: {
                battlesEnabled: application.featureFlags?.battlesEnabled || false,
              }
            }}
            validationSchema={EditApplicationSchema}
            onSubmit={({
              isEnterprise,
              ...values
            }: EditApplicationFormValues) =>
              siteService
                .editApplication({
                  values: {
                    ...values,
                    mainDomainName: values.mainDomainName
                      ? values.mainDomainName
                      : null,
                    applicationType: isEnterprise
                      ? ApplicationType.ENTERPRISE
                      : ApplicationType.NOTOUCH
                  },
                  application
                })
                .then(close)
                .then(action)
            }
            render={(props: FormikProps<EditApplicationFormValues>) => (
              <EditApplication
                application={application}
                formikProps={props}
                close={close}
                translationPath="application.edit"
              />
            )}
          />
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            <CircularProgress color="primary" />
          </div>
        )
      }
    />
  );
}

export default wire(["siteService"], EditApplicationComponent);
